import { Component, inject, Input, OnInit } from '@angular/core';
import { ShowPerformance } from '../../../models/showperformance.model';
import { AlertController, NavController } from '@ionic/angular';
import { CrewRole } from '../../crew-role';
import { TemplatesAdminService } from '../../admin/templates/templates-admin.service';
import { ReadStateService } from '../../../services/read-state.service';
import { AuthStateService } from '../../auth/auth-state.service';

@Component({
    selector: 'app-showreport-button',
    templateUrl: './showreport-button.component.html',
    styleUrls: ['./showreport-button.component.scss'],
    standalone: false
})
export class ShowreportButtonComponent implements OnInit {

  @Input() public performance: ShowPerformance;
  @Input() public activeUserId: number | null = null;

  public hasActiveTemplate = false;
  public isCheckingTemplate = true;

  protected readonly CrewRole = CrewRole;
  private readonly navController: NavController = inject(NavController);
  private readonly alertController: AlertController = inject(AlertController);
  private readonly templatesService: TemplatesAdminService = inject(TemplatesAdminService);
  private readonly readState: ReadStateService = inject(ReadStateService);
  private readonly authStateService: AuthStateService = inject(AuthStateService);

  /**
   * True when this showreport has been created/edited by someone else since the
   * user last opened it (device-local, baseline = first use). Admin-only for
   * now while the feature is being validated in production.
   */
  public get hasUnseenChange(): boolean {
    if (this.authStateService.user()?.isAdmin() !== true) {
      return false;
    }
    const showreport = this.performance?.showreport;
    if (!showreport?.id) {
      return false;
    }
    // updatedBy is only set on edit, so fall back to submittedBy (the creator).
    // Activity = latest of the create/update times, so new showreports surface.
    const activityMs = Math.max(this.readState.toMs(showreport.updatedAt), this.readState.toMs(showreport.createdAt));
    const actorId = showreport.updatedBy?.id ?? showreport.submittedBy?.id;
    return this.readState.isUnseen('showreport', showreport.id, activityMs, actorId, this.activeUserId);
  }

  async ngOnInit(): Promise<void> {
    await this.checkForActiveTemplate();
  }

  private async checkForActiveTemplate(): Promise<void> {
    const show = this.performance?.show;
    if (!show?.id) {
      this.hasActiveTemplate = false;
      this.isCheckingTemplate = false;
      return;
    }

    // Legacy shows always allow creating showreports (uses hardcoded form)
    if (show.useLegacyShowreport) {
      this.hasActiveTemplate = true;
      this.isCheckingTemplate = false;
      return;
    }

    try {
      const template = await this.templatesService.getActiveTemplateForShow(show.id);
      this.hasActiveTemplate = template !== null;
    } catch (error) {
      console.error('[ShowreportButtonComponent] Error checking template:', error);
      this.hasActiveTemplate = false;
    } finally {
      this.isCheckingTemplate = false;
    }
  }

  protected async showShowreport(): Promise<void> {
    if (this.hasShowreport()) {
      await this.navigateToShowreportDetails();
    } else {
      await this.showAddShowreportAlert();
    }
  }

  protected hasShowreport(): boolean {
    return !(
      this.performance?.showreport === null ||      this.performance?.showreport === undefined
    );
  }

  protected isShowreportConcept(): boolean {
    return this.performance?.showreport?.status === 'CONCEPT';
  }

  private async navigateToShowreportDetails(): Promise<void> {
    if (this.performance?.showreport?.id) {
      // ?back=/dashboard so the details page (and any edit→save→details
      // redirect along the way) knows where the user originally came from.
      const queryParams = { back: '/dashboard' };
      if (this.activeUserId === this.performance.showreport.stagemanager?.id && this.performance.showreport.status === 'CONCEPT') {
        await this.navController.navigateForward(['/showreports', this.performance.showreport.id, 'edit'], { queryParams });
      } else {
        await this.navController.navigateForward(['/showreports', this.performance.showreport.id], { queryParams });
      }
    }
  }

  private async showAddShowreportAlert(): Promise<void> {
    // Check if there's an active template for this show
    if (!this.hasActiveTemplate) {
      await this.showNoTemplateAlert();
      return;
    }

    const alert = await this.alertController.create({
      cssClass: 'alert-eventslight',
      header: 'Nieuw showreport aanmaken?',
      message:
        'Er is (nog) geen showreport gemaakt voor dit speelmoment. Wil je deze aanmaken?',
      buttons: [
        {
          text: 'Nee',
          role: 'cancel'
        },
        {
          text: 'Ja',
          role: 'confirm',
          handler: async (): Promise<void> => {
            await this.navigateToAddShowreport();
          }
        }
      ]
    });
    await alert.present();
  }

  private async showNoTemplateAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Geen template beschikbaar',
      message: 'Er is nog geen showreport template geconfigureerd voor deze productie. Vraag een beheerder om een template aan te maken.',
      buttons: ['OK']
    });
    await alert.present();
  }

  private async navigateToAddShowreport(): Promise<void> {
    if (this.performance?.id) {
      await this.navController.navigateForward(['/showreports/add'], {
        queryParams: { performanceId: this.performance.id, back: '/dashboard' }
      });
    }
  }
}
